import { TeamWith } from "@redotech/account-common/base-team";
import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import SettingsIcon from "@redotech/redo-icons/settings-02.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { returnsSettingsTabPath } from "@redotech/redo-merchant-app-common/settings/returns-settings-parent-path";
import { ReturnsSettingsTab } from "@redotech/redo-merchant-app-common/settings/returns-settings-tab";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  UserContext,
  UserContextValue,
} from "@redotech/redo-merchant-app-common/user";
import { Step } from "@redotech/redo-return-core-common/return-flow";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import type { ReturnState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import {
  defaultDropoffFeeUsd,
  DropoffProviderType,
  dropoffProviderType,
  getMerchantFacingProviderName,
  VIRTUAL_PROVIDER,
} from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { isExchangeAgentEnabledForTeam } from "@redotech/redo-return-operations-common/return-flow/exchange-agent/exchange-agent-utils";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { Team } from "@redotech/redo-team-common/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@redotech/redo-web/components/ui/dropdown-menu";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { produce } from "immer";
import { memo, useContext, useMemo, useState } from "react";
import { useFieldEditable } from "../flow-editor/flow-readonly-context";
import { ExchangeAgentConfiguration } from "./exchange-agent-configuration";
import { PerCurrencyOverrides } from "./per-currency-overrides";

export const AdvancedReturnOptions = memo(function AdvancedReturnOptions({
  state,
  setState,
  allSteps,
  step,
  setStateWithId,
  flowType,
}: Pick<
  StepTypeDetailsProps<ReturnState>,
  "state" | "setState" | "allSteps" | "step" | "setStateWithId" | "flowType"
>) {
  const { t } = useTranslation();
  const [
    showInternalOnlyExchangeAgentConfiguration,
    setShowInternalOnlyExchangeAgentConfiguration,
  ] = useState(false);

  const [selectedDropoffProvider, setSelectedDropoffProvider] =
    useState<DropoffProviderType>(VIRTUAL_PROVIDER);

  const user = useContext(UserContext);
  const team = useContext(TeamContext);
  const canEditReturnsSettings = useFieldEditable(
    useHasPermission(SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS),
  );
  const canEditDropoffFees = useFieldEditable(
    canEditReturnsSettings,
    "dropoffFees",
  );
  const canEditFlatShippingCost = useFieldEditable(
    canEditReturnsSettings,
    "flatShippingCost",
  );
  const canEditLabelCostAdjustment = useFieldEditable(
    canEditReturnsSettings,
    "labelCostAdjustment",
  );

  const exchangeAgentConfig = useExchangeAgentOverride({
    team,
    user,
    flowType,
  });

  const handleFlatShipping = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.flatShipping = value;
        // Flat shipping and label upcharge are mutually exclusive
        if (value) {
          state.labelUpcharge = false;
        }
      }),
    );
  });

  const handleFlatShippingRate = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.flatShippingRate = value;
      }),
    );
  });

  const handleFlatShippingCurrencyOverrides = useHandler(
    (overrides: Record<string, string>) => {
      setState((state) =>
        produce(state, (state) => {
          state.flatShippingCurrencyOverrides = Object.fromEntries(
            Object.entries(overrides)
              .filter(([, v]) => v !== undefined && !isNaN(+v))
              .map(([k, v]) => [k, +v]),
          );
        }),
      );
    },
  );

  const handleFlatShippingPerCurrencyAmountsEnabled = useHandler(
    (enabled: boolean) => {
      setState((state) =>
        produce(state, (state) => {
          state.flatShippingPerCurrencyAmountsEnabled = enabled;
        }),
      );
    },
  );

  const handleMerchantLabelAdjustment = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.labelUpcharge = value;
        // Flat shipping and label upcharge are mutually exclusive
        if (value) {
          state.flatShipping = false;
        }
      }),
    );
  });

  const handleLabelUpchargeType = useHandler(
    (value: Step.LabelUpchargeType) => {
      setState((state) =>
        produce(state, (state) => {
          state.labelUpchargeType = value;
        }),
      );
    },
  );

  const handleLabelUpchargeValue = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.labelUpchargeValue = value;
      }),
    );
  });

  const handleDropoffFeesEnabled = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.dropoffFeesEnabled = value;
      }),
    );
  });

  const handleDropoffFeeRemove = useHandler(
    (provider: (typeof dropoffProviderType)[number]) => {
      setState((state) =>
        produce(state, (state) => {
          state.dropoffFees = state.dropoffFees?.filter(
            (f) => f.provider !== provider,
          );
        }),
      );
    },
  );

  const handleDropoffFeeChange = useHandler(
    (provider: (typeof dropoffProviderType)[number], fee: string) => {
      setState((state) =>
        produce(state, (state) => {
          if (!state.dropoffFees) {
            state.dropoffFees = [];
          }
          const entry = state.dropoffFees.find((f) => f.provider === provider);
          if (entry) {
            entry.fee = fee;
          } else {
            state.dropoffFees.push({ provider, fee });
          }
        }),
      );
    },
  );

  const handleDropoffFeeCurrencyOverridesChange = useHandler(
    (
      provider: (typeof dropoffProviderType)[number],
      overrides: Record<string, string>,
    ) => {
      const asNumbers = Object.fromEntries(
        Object.entries(overrides)
          .filter(([, v]) => v !== undefined && !isNaN(+v))
          .map(([k, v]) => [k, +v]),
      );
      setState((state) =>
        produce(state, (state) => {
          if (!state.dropoffFees) {
            state.dropoffFees = [];
          }
          const entry = state.dropoffFees.find((f) => f.provider === provider);
          if (entry) {
            entry.currencyOverrides = asNumbers;
          } else {
            state.dropoffFees.push({
              provider,
              fee: "0.00",
              currencyOverrides: asNumbers,
            });
          }
        }),
      );
    },
  );

  const handleDropoffFeePerCurrencyAmountsEnabledChange = useHandler(
    (
      provider: (typeof dropoffProviderType)[number],
      perCurrencyAmountsEnabled: boolean,
    ) => {
      setState((state) =>
        produce(state, (state) => {
          if (!state.dropoffFees) {
            state.dropoffFees = [];
          }
          const entry = state.dropoffFees.find((f) => f.provider === provider);
          if (entry) {
            entry.perCurrencyAmountsEnabled = perCurrencyAmountsEnabled;
          } else {
            state.dropoffFees.push({
              provider,
              fee: "0.00",
              perCurrencyAmountsEnabled,
            });
          }
        }),
      );
    },
  );

  const handleCoverPackagePickup = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.coverPackagePickup = value;
      }),
    );
  });

  const handleExchangeAgentDisable = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.exchangeAgentOverrides = {
          ...state.exchangeAgentOverrides,
          disableExchangeAgent: value,
        };
      }),
    );
  });

  const handleVariantRecommendationsDisable = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.exchangeAgentOverrides = {
          ...state.exchangeAgentOverrides,
          disableVariantRecommendations: value,
        };
      }),
    );
  });

  const handleProductRecommendationsDisable = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.exchangeAgentOverrides = {
          ...state.exchangeAgentOverrides,
          disableProductRecommendations: value,
        };
      }),
    );
  });

  if (!team) {
    return null;
  }

  return (
    <>
      {showInternalOnlyExchangeAgentConfiguration && (
        <RedoModal
          isOpen={showInternalOnlyExchangeAgentConfiguration}
          modalSize="lg"
          onModalCloseRequested={() =>
            setShowInternalOnlyExchangeAgentConfiguration(false)
          }
          title={t("Internal Only Exchange Agent Configuration")}
        >
          <ExchangeAgentConfiguration
            allSteps={allSteps}
            canEditSettings={canEditReturnsSettings}
            setStateWithId={setStateWithId}
            step={step}
          />
        </RedoModal>
      )}
      <RedoCollapseSubsection title={t("Advanced return options")}>
        <Flex dir="column" gap="sm" mb="md">
          <RedoToggle
            description={t(
              "Flat fee charged to customers per drop-off provider",
            )}
            disabled={!canEditDropoffFees}
            label={t("Drop-off fees")}
            setValue={handleDropoffFeesEnabled}
            size="xs"
            value={state.dropoffFeesEnabled ?? false}
          />
          {(state.dropoffFeesEnabled ?? false) && (
            <Flex dir="column" gap="md" pl="4xl">
              <RedoSingleSelectDropdownInput
                disabled={!canEditDropoffFees}
                label={t("Provider")}
                options={dropoffProviderType.map((provider) => ({
                  type: "text" as const,
                  id: provider,
                  text: getMerchantFacingProviderName(provider),
                  value: provider,
                }))}
                optionSelected={({ item }) =>
                  setSelectedDropoffProvider(item.value)
                }
                selectedItem={{
                  type: "text" as const,
                  id: selectedDropoffProvider,
                  text: getMerchantFacingProviderName(selectedDropoffProvider),
                  value: selectedDropoffProvider,
                }}
              />
              <RedoTextInput
                errorText={(() => {
                  const raw = state.dropoffFees?.find(
                    (f) => f.provider === selectedDropoffProvider,
                  )?.fee;
                  if (raw == null || raw.trim() === "") {
                    return undefined;
                  }
                  const num = parseFloat(raw);
                  if (isNaN(num)) {
                    return t("Invalid amount");
                  }
                  if (num < 0) {
                    return t("Fee must be a positive amount");
                  }
                  return undefined;
                })()}
                fullWidth
                inputTypeHint="number"
                label={t("Fee")}
                onBlur={() => {
                  // Never submit the fee value on blur; clearing or garbage removes the fee so the default applies.
                  const raw = state.dropoffFees?.find(
                    (f) => f.provider === selectedDropoffProvider,
                  )?.fee;
                  if (raw == null) {
                    return;
                  }
                  const num = parseFloat(raw);
                  if (raw.trim() === "" || isNaN(num)) {
                    handleDropoffFeeRemove(selectedDropoffProvider);
                    return;
                  }
                  handleDropoffFeeChange(
                    selectedDropoffProvider,
                    num < 0 ? "0.00" : num.toFixed(2),
                  );
                }}
                placeholder={String(
                  defaultDropoffFeeUsd[selectedDropoffProvider] ?? "",
                )}
                setValue={(value) =>
                  handleDropoffFeeChange(selectedDropoffProvider, value)
                }
                state={canEditDropoffFees ? "default" : "readonly"}
                trailingActions={
                  <Text fontSize="sm" p="xs" textColor="secondary">
                    $
                  </Text>
                }
                value={
                  state.dropoffFees?.find(
                    (f) => f.provider === selectedDropoffProvider,
                  )?.fee ?? ""
                }
              />
              <PerCurrencyOverrides
                baseAmount={
                  state.dropoffFees?.find(
                    (f) => f.provider === selectedDropoffProvider,
                  )?.fee ?? "0"
                }
                currencyOverrides={Object.fromEntries(
                  Object.entries(
                    state.dropoffFees?.find(
                      (f) => f.provider === selectedDropoffProvider,
                    )?.currencyOverrides ?? {},
                  ).map(([k, v]) => [k, String(v)]),
                )}
                disabled={!canEditDropoffFees}
                onCurrencyOverridesChange={(overrides) =>
                  handleDropoffFeeCurrencyOverridesChange(
                    selectedDropoffProvider,
                    overrides,
                  )
                }
                onPerCurrencyAmountsEnabledChange={(enabled) =>
                  handleDropoffFeePerCurrencyAmountsEnabledChange(
                    selectedDropoffProvider,
                    enabled,
                  )
                }
                perCurrencyAmountsEnabled={
                  state.dropoffFees?.find(
                    (f) => f.provider === selectedDropoffProvider,
                  )?.perCurrencyAmountsEnabled ?? false
                }
              />
            </Flex>
          )}
        </Flex>
        <Flex dir="column" gap="sm" mb="md">
          <RedoToggle
            description={t(
              "Charge customers a flat cost for return shipping labels. Merchant will receive/pay for the difference between actual cost.",
            )}
            disabled={!canEditFlatShippingCost || state.labelUpcharge}
            label={t("Flat shipping cost")}
            setValue={handleFlatShipping}
            size="xs"
            value={state.flatShipping || false}
          />
          {state.flatShipping && (
            <Flex dir="column" gap="md" pl="4xl">
              <RedoTextInput
                IconLeading={() => <Text fontSize="xs">$</Text>}
                inputTypeHint="number"
                placeholder="0.00"
                setValue={handleFlatShippingRate}
                state={canEditFlatShippingCost ? "default" : "readonly"}
                value={state.flatShippingRate || ""}
              />
              <PerCurrencyOverrides
                baseAmount={state.flatShippingRate || "0"}
                currencyOverrides={Object.fromEntries(
                  Object.entries(state.flatShippingCurrencyOverrides ?? {}).map(
                    ([k, v]) => [k, String(v)],
                  ),
                )}
                disabled={!canEditFlatShippingCost}
                onCurrencyOverridesChange={handleFlatShippingCurrencyOverrides}
                onPerCurrencyAmountsEnabledChange={
                  handleFlatShippingPerCurrencyAmountsEnabled
                }
                perCurrencyAmountsEnabled={
                  state.flatShippingPerCurrencyAmountsEnabled ?? false
                }
                tooltipText={t(
                  "Specify exact flat shipping costs for specific currencies. Currencies not listed will use the default rate above.",
                )}
              />
            </Flex>
          )}
        </Flex>
        <RedoToggle
          description={t(
            "Adjust the shipping label cost by a fixed amount or percentage. Positive values increase the cost (upcharge), negative values decrease it (discount).",
          )}
          disabled={!canEditLabelCostAdjustment || state.flatShipping}
          label={t("Label cost adjustment")}
          setValue={handleMerchantLabelAdjustment}
          size="xs"
          value={state.labelUpcharge || false}
        />
        {state.labelUpcharge && (
          <Flex dir="column" gap="md" pl="4xl">
            <RedoTextInput
              fullWidth
              inputTypeHint="number"
              setValue={handleLabelUpchargeValue}
              state={canEditLabelCostAdjustment ? "default" : "readonly"}
              trailingActions={
                canEditLabelCostAdjustment ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <RedoButton
                        hierarchy="tertiary"
                        IconTrailing={ChevronDownIcon}
                        size="xs"
                        text={
                          state.labelUpchargeType ===
                          Step.LabelUpchargeType.PERCENTAGE
                            ? "%"
                            : "$"
                        }
                        type="button"
                      />
                    </DropdownMenuTrigger>
                    {/* design-allow: zIndex keeps the dropdown above the settings panel. */}
                    <DropdownMenuContent align="end" style={{ zIndex: 1000 }}>
                      <DropdownMenuItem
                        onClick={() =>
                          handleLabelUpchargeType(Step.LabelUpchargeType.FIXED)
                        }
                      >
                        {t("$ Fixed dollar amount")}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          handleLabelUpchargeType(
                            Step.LabelUpchargeType.PERCENTAGE,
                          )
                        }
                      >
                        {t("% Percentage")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Text fontSize="sm" p="xs" textColor="secondary">
                    {state.labelUpchargeType ===
                    Step.LabelUpchargeType.PERCENTAGE
                      ? "%"
                      : "$"}
                  </Text>
                )
              }
              value={state.labelUpchargeValue || "0"}
            />
          </Flex>
        )}
        {flowType !== FlowType.WITHDRAWAL && team.settings.pickupEnabled && (
          <RedoToggle
            description={t(
              "Merchant covers package pickup costs up to the max amount set in the shipping settings {{maxAmount}}",
              {
                maxAmount: team.settings.pickupMaxCoveredAmountEnabled
                  ? `($${team.settings.pickupMaxCoveredAmount?.toFixed(2) ?? "0.00"})`
                  : t("(no max set)"),
              },
            )}
            disabled={!canEditReturnsSettings}
            label={t("Cover package pickup")}
            setValue={handleCoverPackagePickup}
            size="xs"
            value={state.coverPackagePickup || false}
          />
        )}
        {exchangeAgentConfig.exchangeAgentEnabled && (
          <Flex dir="column">
            <Divider />
            <RedoExternalLink
              fontSize="md"
              text={t("Exchange Agent settings")}
              url={exchangeAgentConfig.settingsLink}
            />
            <Flex align="center" dir="row" justify="space-between">
              <RedoToggle
                description={t("Don't use Exchange Agent for this step")}
                disabled={!canEditReturnsSettings}
                label={t("Disable Exchange Agent")}
                setValue={handleExchangeAgentDisable}
                size="xs"
                value={
                  state.exchangeAgentOverrides?.disableExchangeAgent ?? false
                }
              />
              <Flex dir="column" gap="xxs">
                <Flex align="center" dir="row" gap="sm">
                  {exchangeAgentConfig.displayInternalOnly && (
                    <Tooltip title={t("Internal only Exchange Agent settings")}>
                      <RedoButton
                        aria-label={t("Settings")}
                        disabled={!canEditReturnsSettings}
                        hierarchy="tertiary"
                        IconLeading={SettingsIcon}
                        onClick={(event) => {
                          setShowInternalOnlyExchangeAgentConfiguration(true);
                          event.stopPropagation();
                        }}
                        size="sm"
                        theme="normal"
                      />
                    </Tooltip>
                  )}
                </Flex>
              </Flex>
            </Flex>
            {!state.exchangeAgentOverrides?.disableExchangeAgent && (
              <Flex dir="column">
                <RedoToggle
                  description={t(
                    "Don't recommend variant exchanges for this step",
                  )}
                  disabled={
                    !canEditReturnsSettings ||
                    !exchangeAgentConfig.variantRecommendationsEnabled
                  }
                  label={t("Disable variant recommendations")}
                  setValue={handleVariantRecommendationsDisable}
                  size="xs"
                  value={
                    state.exchangeAgentOverrides
                      ?.disableVariantRecommendations ?? false
                  }
                />
                {exchangeAgentConfig.productRecommendationsEnabled && (
                  <RedoToggle
                    description={t(
                      "Don't recommend product exchanges for this step",
                    )}
                    disabled={
                      !canEditReturnsSettings ||
                      !exchangeAgentConfig.productRecommendationsEnabled
                    }
                    label={t("Disable product recommendations")}
                    setValue={handleProductRecommendationsDisable}
                    size="xs"
                    value={
                      state.exchangeAgentOverrides
                        ?.disableProductRecommendations ?? false
                    }
                  />
                )}
                {(!exchangeAgentConfig.variantRecommendationsEnabled ||
                  !exchangeAgentConfig.productRecommendationsEnabled) && (
                  <Flex dir="column" pt="md">
                    {!exchangeAgentConfig.variantRecommendationsEnabled && (
                      <Text fontSize="sm" textOverflow="wrap">
                        {t("Variant recommendations are currently disabled")}
                      </Text>
                    )}
                    {!exchangeAgentConfig.productRecommendationsEnabled && (
                      <Text fontSize="sm" textOverflow="wrap">
                        {t("Product recommendations are currently disabled")}
                      </Text>
                    )}
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>
        )}
      </RedoCollapseSubsection>
    </>
  );
});

const useExchangeAgentOverride = ({
  team,
  user,
  flowType,
}: {
  team:
    | TeamWith<{
        exchangeAgent: Team["settings"]["exchangeAgent"];
        returnsAiExchange: Team["settings"]["returnsAiExchange"];
        shopifySubscription?: Team["settings"]["shopifySubscription"];
      }>
    | undefined;
  user: UserContextValue | undefined;
  flowType: FlowType;
}): {
  exchangeAgentEnabled: boolean;
  displayInternalOnly?: boolean;
  variantRecommendationsEnabled?: boolean;
  productRecommendationsEnabled?: boolean;
  settingsLink?: string;
} => {
  const {
    exchangeAgentEnabled,
    variantRecommendationsEnabled,
    productRecommendationsEnabled,
  } = useMemo(() => {
    if (!team || flowType !== FlowType.RETURN) {
      return {
        exchangeAgentEnabled: false,
        variantRecommendationsEnabled: false,
        productRecommendationsEnabled: false,
      };
    }
    const exchangeAgentEnabled = isExchangeAgentEnabledForTeam(
      team.settings.returnsAiExchange,
      team.settings.exchangeAgent,
      team,
    );
    const variantRecommendationsEnabled =
      team.settings.exchangeAgent.variantRecommendations.enabled;
    const productRecommendationsEnabled =
      team.settings.exchangeAgent.productRecommendations.enabled;
    return {
      exchangeAgentEnabled,
      variantRecommendationsEnabled,
      productRecommendationsEnabled,
    };
  }, [team, flowType]);

  if (!exchangeAgentEnabled) {
    return { exchangeAgentEnabled };
  }

  return {
    exchangeAgentEnabled,
    displayInternalOnly: !!(user?.email && /@getredo\.com/.test(user?.email)),
    variantRecommendationsEnabled,
    productRecommendationsEnabled,
    settingsLink: `${window.location.href.split("/settings")[0]}/settings/${returnsSettingsTabPath(ReturnsSettingsTab.PORTAL)}`,
  };
};
